import React, { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import DrawCanvas from "./DrawCanvas";
import DrawToolbar from "./DrawToolbar";
import { PALETTE, SIZES } from "./strokes";
import useDrawer from "./useDrawer";

const Notice = ({ title, body }) => (
  <div className="w-full max-w-md mx-auto bg-white rounded-3xl shadow-2xl p-8 text-center" dir="rtl">
    <h2 className="text-2xl font-bold text-gray-900 mb-3">{title}</h2>
    <p className="text-gray-600 text-lg">{body}</p>
  </div>
);

/**
 * The scanning phone's side of draw mode: shows the word, then — once the
 * player taps start — the canvas and tools. `code` is the board's session
 * code from the QR; without it the drawing stays on this phone only.
 */
const DrawerPanel = ({ qrCode, code, gameConfig }) => {
  const drawer = useDrawer(code);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState(PALETTE[0]);
  const [sizeIndex, setSizeIndex] = useState(1);
  const [wordHidden, setWordHidden] = useState(false);
  const GameIcon = gameConfig.icon;

  if (drawer.phase === "taken") {
    return (
      <Notice
        title="في لاعب ثاني يرسم"
        body="هذا السؤال مسحه لاعب آخر وبدأ الرسم."
      />
    );
  }
  if (drawer.phase === "gone") {
    return (
      <Notice
        title="انتهت جولة الرسم"
        body="امسح الرمز من شاشة اللعبة مرة ثانية إذا السؤال ما زال مفتوح."
      />
    );
  }

  if (!drawer.started) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div
            className={`inline-flex items-center gap-3 bg-gradient-to-r ${gameConfig.gradient} text-white px-8 py-4 rounded-2xl shadow-2xl`}
          >
            <GameIcon className="text-3xl" />
            <span className="text-2xl font-bold">{gameConfig.name}</span>
          </div>
        </div>
        <div className="bg-white rounded-3xl shadow-2xl p-8" dir="rtl">
          <h3 className="text-gray-500 text-lg mb-3 font-medium">
            ارسم هذه الكلمة لفريقك:
          </h3>
          <div
            className={`bg-gradient-to-r ${gameConfig.lightBg} rounded-2xl p-8 border-4 ${gameConfig.border} mb-6`}
          >
            <p
              className={`text-4xl md:text-5xl font-bold text-center ${gameConfig.text}`}
            >
              {qrCode.answer}
            </p>
          </div>
          {drawer.phase === "local" && (
            <p className="text-gray-500 text-center mb-4">
              الرسم راح يظهر على جوالك بس — ورِّ فريقك الشاشة.
            </p>
          )}
          <button
            onClick={drawer.start}
            disabled={drawer.phase === "connecting"}
            className={`w-full bg-gradient-to-r ${gameConfig.gradient} text-white text-xl font-bold py-4 rounded-xl shadow-md transition-all duration-300 disabled:opacity-50`}
          >
            {drawer.phase === "connecting" ? "جاري الاتصال..." : "ابدأ الرسم"}
          </button>
        </div>
      </div>
    );
  }

  const brush = { tool, color, size: SIZES[sizeIndex] };

  return (
    <div className="w-full max-w-md mx-auto space-y-3">
      <button
        dir="rtl"
        onClick={() => setWordHidden((hidden) => !hidden)}
        className="w-full bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center justify-between"
      >
        <span className={`text-xl font-bold ${gameConfig.text}`}>
          {wordHidden ? "••••••" : qrCode.answer}
        </span>
        <span className="text-gray-500 text-lg">
          {wordHidden ? <FaEye /> : <FaEyeSlash />}
        </span>
      </button>

      <DrawCanvas
        store={drawer.store}
        onStrokeStart={(pt) => drawer.strokeStart(pt, brush)}
        onStrokeMove={drawer.strokeMove}
        onStrokeEnd={drawer.strokeEnd}
        className="w-full aspect-square shadow-xl"
      />

      <DrawToolbar
        tool={tool}
        color={color}
        sizeIndex={sizeIndex}
        onPickColor={(c) => {
          setColor(c);
          setTool("pen");
        }}
        onPickSize={setSizeIndex}
        onPickEraser={() => setTool("eraser")}
        onUndo={drawer.undo}
        onClear={drawer.clear}
      />
    </div>
  );
};

export default DrawerPanel;
